import { IAccountMappingTemplateGroup, ItemType } from '@modeso/types__aumico-lib-annualreport-be';
import { MaskingHelper } from './MaskingHelper';

interface TraversalOptions {
    state?: { [key: string]: string[] }; // If provided, populates this map
    mutate?: boolean;                    // If true, adds sortingId to the objects
}

export class GroupingPathHelper {

    /**
     * Generates the complete sorting state map for a given root group.
     */
    public static generateFullSortingState(
        rootGroup: IAccountMappingTemplateGroup,
        rootPath: string = ''
    ): { [containerId: string]: string[] } {
        const sortingState: { [containerId: string]: string[] } = {};
        this.traverseGroup(rootGroup, rootPath, { state: sortingState });
        return sortingState;
    }

    /**
     * Traverses the root group and injects the generated 'sortingId' (path) into items.
     */
    public static generateAndAddFullSortingState(
        rootGroup: IAccountMappingTemplateGroup,
        rootPath: string = ''
    ): IAccountMappingTemplateGroup {
        this.traverseGroup(rootGroup, rootPath, { mutate: true });
        return rootGroup;
    }

    // ==========================================
    // --- SORTING APPLICATION LOGIC ---
    // ==========================================

    /**
     * Sorts the accounts within an item (FLI) and its nested Sub-FLIs based on the provided sorting state.
     * This mutates the item.accounts arrays in place.
     * @param item The FLI or Item containing accounts
     * @param sortingState The map of { [sortingId]: string[] } defining the order
     */
    public static applySortingStateToItem(
        item: any,
        sortingState: { [key: string]: string[] }
    ): void {
        if (!item || !item.accounts || !sortingState) return;

        // 1. Sort the immediate accounts of this item
        if (item.sortingId && sortingState[item.sortingId]) {
            this.sortAccountsArray(item.accounts, sortingState[item.sortingId]);
        }

        // 2. Recursively sort Sub-FLIs
        item.accounts.forEach((acc: any) => {
            if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                if (acc.sortingId) {
                    this.applySortingStateToItem(acc, sortingState);
                }
            }
        });
    }

    /**
     * Helper to sort a specific array of accounts based on an ID list.
     */
    private static sortAccountsArray(accounts: any[], orderIds: string[]): void {
        if (!orderIds || orderIds.length === 0) return;

        const indexMap = new Map(orderIds.map((id, i) => [id, i]));

        /**
         * Position of a row in the saved order.
         *
         * Tries the row-scoped identifier before the 2-part one, so a saved order that distinguishes
         * two instances of a duplicate is honoured while an older order (one entry for both) still
         * resolves — both instances then land on the same position and keep their relative order,
         * exactly as before row identity existed.
         */
        const positionOf = (row: any): number | undefined => {
            const isGroupRow = row.groupId && Array.isArray(row.accounts);
            const accNum = isGroupRow ? row.groupId : (row.accountNumber || row.groupId);
            const candidates = isGroupRow
                ? [MaskingHelper.getAccountIdentifier(accNum, row.originalName ?? row.name, row.shareholder)]
                : MaskingHelper.getIdentifierCandidates({ ...row, accountNumber: accNum });
            for (const id of candidates) {
                const index = indexMap.get(id);
                if (index !== undefined) return index;
            }
            return undefined;
        };

        accounts.sort((a, b) => {
            const indexA = positionOf(a);
            const indexB = positionOf(b);

            if (indexA !== undefined && indexB !== undefined) return indexA - indexB;
            if (indexA !== undefined) return -1;
            if (indexB !== undefined) return 1;
            return 0;
        });
    }

    // ==========================================
    // --- UNIFIED RECURSIVE TRAVERSAL ---
    // ==========================================

    private static traverseGroup(
        group: IAccountMappingTemplateGroup | any,
        parentPath: string,
        opts: TraversalOptions
    ): void {
        if (!group) return;

        // 1. Handle nested 'groups' array
        if (group.groups && Array.isArray(group.groups)) {
            group.groups.forEach((g: any) => {
                const path = this.getGroupPath(parentPath, g);
                if (opts.mutate) g.sortingId = path;
                this.traverseGroup(g, path, opts);
            });
        }

        // 2. Handle 'sortedItems'
        if (group.sortedItems && Array.isArray(group.sortedItems)) {
            group.sortedItems.forEach((item: any) => {
                if (this.isGroup(item)) {
                    // Nested Group
                    const path = this.getGroupPath(parentPath, item);
                    if (opts.mutate) item.sortingId = path;
                    this.traverseGroup(item, path, opts);
                } else if (item.type === ItemType.FLI || item.type === 'FLI') {
                    // FLI
                    const path = this.getFliPath(parentPath, item);
                    if (opts.mutate) item.sortingId = path;
                    this.traverseAccountContainer(item, path, opts);
                }
            });
        }
    }

    /**
     * Handles both FLIs and Sub-FLIs since they share the same 'accounts' structure logic.
     */
    private static traverseAccountContainer(
        container: any,
        containerPath: string,
        opts: TraversalOptions
    ): void {
        if (!container.accounts) return;

        // 1. Capture State (if requested)
        if (opts.state) {
            opts.state[containerPath] = container.accounts.map((acc: any) => {
                // FIX: Prioritize groupId for Groups during State Generation
                const isGroup = acc.groupId && Array.isArray(acc.accounts);
                const accNum = isGroup ? acc.groupId : (acc.accountNumber || acc.groupId);

                // Row-scoped for a true duplicate (see MaskingHelper.getRowIdentifier), so the two
                // instances hold two positions in the captured order instead of one shared entry.
                return isGroup
                    ? MaskingHelper.getAccountIdentifier(accNum, acc.originalName ?? acc.name, acc.shareholder)
                    : MaskingHelper.getRowIdentifier({ ...acc, accountNumber: accNum });
            });
        }

        // 2. Recurse into Sub-FLIs and set IDs
        container.accounts.forEach((acc: any) => {
            if (opts.mutate) {
                acc.sortingId = containerPath;
            }

            if (acc.type === 'SUB FLI' || acc.type === 'OF_THAT_ITEM') {
                const subFliPath = this.getSubFliPath(containerPath, acc);

                // If it is a Sub-FLI, we overwrite the generic parent path 
                // with its own specific path so it can act as a container.
                if (opts.mutate) {
                    acc.sortingId = subFliPath;
                }

                this.traverseAccountContainer(acc, subFliPath, opts);
            }
        });
    }

    // ==========================================
    // --- ID GENERATION LOGIC ---
    // ==========================================

    public static getGroupPath(parentPath: string, group: any): string {
        // A translated print copy keeps the report-language title (TemplateTranslatorVisitor): the
        // stored paths were built from it, so "Assets" must still resolve to "Aktiven".
        const title = group.originalTitle ?? group.title;
        const identifier = `${title || 'group'}_${group.rowNo || 0}`;
        return `${parentPath}__grp__${this.sanitizeId(identifier)}`;
    }

    public static getFliPath(parentPath: string, fli: any): string {
        const identifier = `${fli.name || fli.caption || 'fli'}_${fli.rowNo || 0}`;
        return `${parentPath}__fli__${this.sanitizeId(identifier)}`;
    }

    public static getSubFliPath(parentFliPath: string, subFli: any): string {
        const identifier = `${subFli.name || subFli.caption || 'subfli'}_${subFli.rowNo || 0}`;
        return `${parentFliPath}__subfli__${this.sanitizeId(identifier)}`;
    }

    public static sanitizeId(str: string): string {
        return str.replace(/[^a-zA-Z0-9_-]/g, '_');
    }

    private static isGroup(item: any): boolean {
        return item && 'sortedItems' in item;
    }

    // ==========================================
    // --- Look up ---
    // ==========================================

    /**
     * Generates a unique path for a subtotal
     */
    public static getSubtotalPath(parentPath: string, subtotal: any): string {
        const sanitizedName = this.sanitizeId(subtotal.name || subtotal.caption || '');
        return `${parentPath}__subtotal__${sanitizedName}`;
    }

    public static findFliBySortingId(
        rootGroup: IAccountMappingTemplateGroup,
        sortingId: string
    ): any | null {
        let foundFli: any | null = null;

        const traverse = (group: IAccountMappingTemplateGroup | any) => {
            if (foundFli) return; // Early exit if found

            // Check FLIs in sortedItems
            if (group.sortedItems && Array.isArray(group.sortedItems)) {
                for (const item of group.sortedItems) {
                    if (item.type === ItemType.FLI || item.type === 'FLI') {
                        if (item.sortingId === sortingId) {
                            foundFli = item;
                            return;
                        }
                    } else if (this.isGroup(item)) {
                        traverse(item); // Recurse into nested group
                    }
                }
            }

            // Recurse into nested groups
            if (group.groups && Array.isArray(group.groups)) {
                for (const g of group.groups) {
                    traverse(g);
                }
            }
        };

        traverse(rootGroup);
        return foundFli;
    }
}